/**
 * @engine/vfx - VFX Graph
 *
 * Graph model specialized for VFX systems with context support.
 */

import { Graph, uid, type Vec2Like, type BaseGraphData } from '@engine/graph';
import type { ContextInstance } from '@engine/graph';
import type {
  VFXNodeInstance,
  VFXGraphVariable,
  VFXGraphData,
  VFXContextType,
  VFXContextDefinition,
  VFXContextFlowEdge,
  VFXUpdateContextSettings,
  VFXInitializeContextSettings,
  VFXAnyContextSettings,
  VFXContextSettings,
  VFXEventTriggerConfig,
} from './types';
import { VFX_CONTEXTS } from './types';
import { vfxNodeRegistry } from './VFXNodeRegistry';

/**
 * VFX Graph - A specialized graph for particle effects.
 *
 * Features:
 * - Context blocks (Spawn, Initialize, Update, Output)
 * - Nodes belong to contexts
 * - Context flow edges (Spawn → Init → Update → Output)
 * - Context-aware validation
 */
export class VFXGraph extends Graph<VFXNodeInstance, VFXGraphVariable> {
  private readonly _contexts = new Map<string, ContextInstance>();
  private readonly _contextFlowEdges = new Map<string, VFXContextFlowEdge>();
  private readonly _contextSettings = new Map<string, VFXAnyContextSettings>();

  /**
   * Create a new VFX graph with default contexts.
   */
  constructor() {
    super();
    this._createDefaultContexts();
  }

  /**
   * Create default VFX contexts.
   * Note: 'event' is NOT created by default - it's optional
   */
  private _createDefaultContexts(): void {
    // Only create required contexts (event is optional)
    const contextTypes: VFXContextType[] = ['spawn', 'initialize', 'update', 'output'];
    let y = 50;

    for (const type of contextTypes) {
      const def = VFX_CONTEXTS[type];
      const ctx: ContextInstance = {
        id: uid('ctx'),
        type: def.id,
        position: { x: 50, y },
        size: { width: 320, height: 180 },
        nodeIds: [],
        collapsed: false,
      };
      this._contexts.set(ctx.id, ctx);
      y += 230; // Space between contexts for flow arrows
    }
  }

  // ============================================================================
  // Context Management
  // ============================================================================

  /**
   * Get all context instances.
   */
  get contexts(): readonly ContextInstance[] {
    return [...this._contexts.values()];
  }

  /**
   * Get context by ID.
   */
  getContext(id: string): ContextInstance | undefined {
    return this._contexts.get(id);
  }

  /**
   * Get context by type.
   */
  getContextByType(type: VFXContextType): ContextInstance | undefined {
    const defId = VFX_CONTEXTS[type].id;
    // Support both 'initialize' and 'vfx.initialize' style type names
    return [...this._contexts.values()].find((c) => c.type === defId || c.type === type);
  }

  /**
   * Get context definition for a context instance.
   */
  getContextDefinition(contextId: string): VFXContextDefinition | undefined {
    const ctx = this._contexts.get(contextId);
    if (!ctx) return undefined;
    
    for (const [, def] of Object.entries(VFX_CONTEXTS)) {
      if (def.id === ctx.type) return def;
    }
    return undefined;
  }

  /**
   * Add an Event context (optional context).
   */
  addEventContext(position: Vec2Like = { x: 50, y: -150 }): ContextInstance {
    return this.addContext('event', position);
  }

  /**
   * Add a context of any type.
   */
  addContext(type: VFXContextType, position: Vec2Like = { x: 50, y: 50 }): ContextInstance {
    const def = VFX_CONTEXTS[type];
    if (!def) {
      throw new Error(`Unknown context type: ${type}`);
    }
    
    const ctx: ContextInstance = {
      id: uid('ctx'),
      type: def.id,
      position: { x: position.x, y: position.y },
      size: { width: 320, height: 120 },
      nodeIds: [],
      collapsed: false,
    };
    this._contexts.set(ctx.id, ctx);
    
    // Auto-add default Transit node for GPU Event context
    if (type === 'event') {
      this.addNodeToContext('vfx.event.transit', ctx.id, { x: 0, y: 0 });
    }
    
    return ctx;
  }

  /**
   * Remove a context.
   */
  removeContext(contextId: string): boolean {
    const ctx = this._contexts.get(contextId);
    if (!ctx) return false;
    
    // Remove all nodes in this context
    for (const nodeId of [...ctx.nodeIds]) {
      this.removeNode(nodeId);
    }
    
    // Remove all flow edges connected to this context
    for (const [edgeId, edge] of this._contextFlowEdges) {
      if (edge.fromContextId === contextId || edge.toContextId === contextId) {
        this._contextFlowEdges.delete(edgeId);
      }
    }
    
    this._contexts.delete(contextId);
    return true;
  }

  // ============================================================================
  // Context Flow Edges
  // ============================================================================

  /**
   * Get all context flow edges.
   */
  get contextFlowEdges(): readonly VFXContextFlowEdge[] {
    return [...this._contextFlowEdges.values()];
  }

  /**
   * Add a context flow edge.
   */
  addContextFlowEdge(
    fromContextId: string,
    fromPortId: string,
    toContextId: string,
    toPortId: string
  ): string | null {
    // Validate contexts exist
    const fromCtx = this._contexts.get(fromContextId);
    const toCtx = this._contexts.get(toContextId);
    if (!fromCtx || !toCtx) {
      console.warn('[VFXGraph] Context not found for flow edge');
      return null;
    }
    
    // Validate port types match
    const fromDef = this.getContextDefinition(fromContextId);
    const toDef = this.getContextDefinition(toContextId);
    if (!fromDef || !toDef) return null;
    
    const fromPort = fromDef.flowOutputs?.find(p => p.id === fromPortId);
    const toPort = toDef.flowInputs?.find(p => p.id === toPortId);
    if (!fromPort || !toPort) {
      console.warn('[VFXGraph] Flow port not found');
      return null;
    }
    
    // Type compatibility check
    if (fromPort.type !== toPort.type) {
      console.warn(`[VFXGraph] Flow type mismatch: ${fromPort.type} → ${toPort.type}`);
      return null;
    }
    
    // Check if this connection already exists
    for (const edge of this._contextFlowEdges.values()) {
      if (edge.fromContextId === fromContextId && 
          edge.fromPortId === fromPortId &&
          edge.toContextId === toContextId &&
          edge.toPortId === toPortId) {
        return null; // Already exists
      }
    }
    
    // Check if input port already has a connection (inputs can only have one)
    for (const edge of this._contextFlowEdges.values()) {
      if (edge.toContextId === toContextId && edge.toPortId === toPortId) {
        console.warn('[VFXGraph] Input port already connected');
        return null;
      }
    }
    
    const id = uid('edge');
    const edge: VFXContextFlowEdge = {
      id,
      fromContextId,
      fromPortId,
      toContextId,
      toPortId,
    };
    this._contextFlowEdges.set(id, edge);
    return id;
  }

  /**
   * Remove a context flow edge.
   */
  removeContextFlowEdge(edgeId: string): boolean {
    return this._contextFlowEdges.delete(edgeId);
  }

  /**
   * Get context flow edge by ID.
   */
  getContextFlowEdge(edgeId: string): VFXContextFlowEdge | undefined {
    return this._contextFlowEdges.get(edgeId);
  }

  /**
   * Check if a context has an incoming flow connection.
   */
  hasIncomingFlow(contextId: string, portId?: string): boolean {
    for (const edge of this._contextFlowEdges.values()) {
      if (edge.toContextId === contextId) {
        if (!portId || edge.toPortId === portId) return true;
      }
    }
    return false;
  }

  /**
   * Check if a context has an outgoing flow connection.
   */
  hasOutgoingFlow(contextId: string, portId?: string): boolean {
    for (const edge of this._contextFlowEdges.values()) {
      if (edge.fromContextId === contextId) {
        if (!portId || edge.fromPortId === portId) return true;
      }
    }
    return false;
  }

  /**
   * Get downstream context (what this context flows into).
   */
  getDownstreamContext(contextId: string, portId = 'spawn_event'): ContextInstance | undefined {
    for (const edge of this._contextFlowEdges.values()) {
      if (edge.fromContextId === contextId && edge.fromPortId === portId) {
        return this._contexts.get(edge.toContextId);
      }
    }
    return undefined;
  }

  /**
   * Get upstream context (what flows into this context).
   */
  getUpstreamContext(contextId: string, portId?: string): ContextInstance | undefined {
    for (const edge of this._contextFlowEdges.values()) {
      if (edge.toContextId === contextId) {
        if (!portId || edge.toPortId === portId) {
          return this._contexts.get(edge.fromContextId);
        }
      }
    }
    return undefined;
  }

  // ============================================================================
  // Context Settings (trigger config, capacity, etc.)
  // ============================================================================

  /**
   * Get settings for a context (generic).
   */
  getContextSettings(contextId: string): VFXAnyContextSettings | undefined {
    return this._contextSettings.get(contextId);
  }

  /**
   * Set settings for a context (generic).
   */
  setContextSettings(contextId: string, settings: VFXAnyContextSettings): void {
    this._contextSettings.set(contextId, settings);
  }

  /**
   * Get Initialize context settings (capacity, bounds).
   */
  getInitializeSettings(): VFXInitializeContextSettings | undefined {
    const initCtx = this.getContextByType('initialize');
    if (!initCtx) return undefined;
    return this._contextSettings.get(initCtx.id) as VFXInitializeContextSettings | undefined;
  }

  /**
   * Set Initialize context settings (capacity, bounds).
   */
  setInitializeSettings(settings: Partial<VFXInitializeContextSettings>): void {
    const initCtx = this.getContextByType('initialize');
    if (!initCtx) return;
    
    const existing = this._contextSettings.get(initCtx.id) as VFXInitializeContextSettings | undefined;
    const defaults: VFXInitializeContextSettings = {
      capacity: 10000,
      boundsMode: 'auto',
    };
    this._contextSettings.set(initCtx.id, { ...defaults, ...existing, ...settings });
  }

  /**
   * Get capacity for this system.
   */
  getCapacity(): number {
    return this.getInitializeSettings()?.capacity ?? 10000;
  }

  /**
   * Set capacity for this system.
   */
  setCapacity(capacity: number): void {
    this.setInitializeSettings({ capacity });
  }

  /**
   * Get capacity for a specific Initialize context.
   */
  getContextCapacity(contextId: string): number {
    const settings = this._contextSettings.get(contextId) as VFXInitializeContextSettings | undefined;
    return settings?.capacity ?? 10000;
  }

  /**
   * Set capacity for a specific Initialize context.
   */
  setContextCapacity(contextId: string, capacity: number): void {
    const existing = this._contextSettings.get(contextId) as VFXInitializeContextSettings | undefined;
    const defaults: VFXInitializeContextSettings = {
      capacity: 10000,
      boundsMode: 'auto',
    };
    this._contextSettings.set(contextId, { ...defaults, ...existing, capacity });
  }

  /**
   * Get trigger config for Update context.
   */
  getUpdateTriggerConfig(): VFXEventTriggerConfig | undefined {
    const updateCtx = this.getContextByType('update');
    if (!updateCtx) return undefined;
    const settings = this._contextSettings.get(updateCtx.id) as VFXUpdateContextSettings | undefined;
    return settings?.trigger;
  }

  /**
   * Set trigger config for Update context.
   */
  setUpdateTriggerConfig(config: VFXEventTriggerConfig): void {
    const updateCtx = this.getContextByType('update');
    if (!updateCtx) return;
    
    const existing = this._contextSettings.get(updateCtx.id) as VFXUpdateContextSettings | undefined ?? {};
    this._contextSettings.set(updateCtx.id, { ...existing, trigger: config });
  }

  /**
   * Get nodes in a context.
   */
  getNodesInContext(contextId: string): readonly VFXNodeInstance[] {
    const ctx = this._contexts.get(contextId);
    if (!ctx) return [];
    return ctx.nodeIds.map((id) => this.getNode(id)).filter(Boolean) as VFXNodeInstance[];
  }

  // ============================================================================
  // Node Management (with context)
  // ============================================================================

  /**
   * Add a node to a specific context.
   */
  addNodeToContext(
    nodeType: string,
    contextId: string,
    position: Vec2Like,
    inputValues: Record<string, unknown> = {}
  ): VFXNodeInstance | null {
    const ctx = this._contexts.get(contextId);
    if (!ctx) {
      console.warn(`[VFXGraph] Context not found: ${contextId}`);
      return null;
    }

    // Get context definition to check maxBlocks
    const ctxDef = this.getContextDefinition(contextId);
    if (ctxDef?.maxBlocks !== undefined && ctx.nodeIds.length >= ctxDef.maxBlocks) {
      console.warn(`[VFXGraph] Context ${ctxDef.name} can only have ${ctxDef.maxBlocks} block(s)`);
      return null;
    }

    // Validate node can be in this context
    const def = vfxNodeRegistry.get(nodeType);
    if (!def) {
      console.warn(`[VFXGraph] Node type not found: ${nodeType}`);
      return null;
    }

    const contextType = this._getContextType(ctx.type);
    if (contextType && !vfxNodeRegistry.canUseInContext(nodeType, contextType)) {
      console.warn(`[VFXGraph] Node ${nodeType} cannot be used in ${contextType} context`);
      return null;
    }

    // Add node
    const nodeId = this.addNode(nodeType, position, inputValues);
    const node = this.getNode(nodeId) as VFXNodeInstance | undefined;
    if (!node) return null;

    // Assign to context
    (node as { contextId: string }).contextId = contextId;
    (ctx.nodeIds as string[]).push(nodeId);

    return node;
  }

  /**
   * Remove node (also removes from context).
   */
  override removeNode(nodeId: string): boolean {
    const node = this.getNode(nodeId);
    if (!node) return false;
    
    // Check if node is locked
    const def = vfxNodeRegistry.get(node.type);
    if (def?.locked) {
      console.warn(`[VFXGraph] Cannot remove locked node: ${def.name}`);
      return false;
    }
    
    if (node.contextId) {
      const ctx = this._contexts.get(node.contextId);
      if (ctx) {
        const idx = ctx.nodeIds.indexOf(nodeId);
        if (idx >= 0) {
          (ctx.nodeIds as string[]).splice(idx, 1);
        }
      }
    }
    return super.removeNode(nodeId);
  }

  /**
   * Move node to different context.
   */
  moveNodeToContext(nodeId: string, newContextId: string): boolean {
    const node = this.getNode(nodeId);
    if (!node) return false;

    const def = vfxNodeRegistry.get(node.type);
    if (!def) return false;

    // Validate new context
    const newCtx = this._contexts.get(newContextId);
    if (!newCtx) return false;

    const contextType = this._getContextType(newCtx.type);
    if (contextType && !vfxNodeRegistry.canUseInContext(node.type, contextType)) {
      return false;
    }

    // Remove from old context
    if (node.contextId) {
      const oldCtx = this._contexts.get(node.contextId);
      if (oldCtx) {
        const idx = oldCtx.nodeIds.indexOf(nodeId);
        if (idx >= 0) {
          (oldCtx.nodeIds as string[]).splice(idx, 1);
        }
      }
    }

    // Add to new context
    (node as { contextId: string }).contextId = newContextId;
    (newCtx.nodeIds as string[]).push(nodeId);

    return true;
  }

  // ============================================================================
  // Serialization
  // ============================================================================

  /**
   * Convert to serializable data.
   */
  toVFXData(): VFXGraphData {
    const baseData = super.toData();
    const contextSettings = this.getContextSettingsMap();

    return {
      ...baseData,
      contexts: [...this._contexts.values()],
      contextFlowEdges: [...this._contextFlowEdges.values()],
      contextSettings: Object.keys(contextSettings).length > 0 ? contextSettings : undefined,
    };
  }

  /** Snapshot of per-context settings (capacity / trigger config) keyed by context id. */
  getContextSettingsMap(): VFXContextSettings {
    const out: Record<string, VFXAnyContextSettings> = {};
    for (const [id, settings] of this._contextSettings) out[id] = settings;
    return out;
  }

  /**
   * Update a graph variable in place by id (immutable patch). Replaces the
   * remove-then-`addVariableWithId` dance callers had to do.
   */
  updateVariable(id: string, patch: Partial<VFXGraphVariable>): boolean {
    const existing = this.getVariable(id);
    if (!existing) return false;
    this.removeVariable(id);
    this.addVariableWithId({ ...existing, ...patch, id } as VFXGraphVariable);
    return true;
  }

  /**
   * Create graph from VFX data.
   */
  static createFromVFXData(data: VFXGraphData): VFXGraph {
    const graph = new VFXGraph();

    // Clear default contexts and edges
    graph._contexts.clear();
    graph._contextFlowEdges.clear();

    // Restore the graph name (previously dropped — the host tracked it separately).
    if (data.name) graph.name = data.name;

    // Restore variables
    if (data.variables) {
      for (const variable of data.variables) {
        graph.addVariableWithId(variable);
      }
    }

    // Restore contexts (nodeIds will be populated below)
    if (data.contexts) {
      for (const ctx of data.contexts) {
        graph._contexts.set(ctx.id, {
          ...ctx,
          nodeIds: [], // Will be populated from nodes
        });
      }
    }

    // Restore nodes with original IDs
    for (const node of data.nodes) {
      graph.addNodeWithId(node as VFXNodeInstance);
      
      // Add node to context's nodeIds if it has a contextId
      const vfxNode = node as VFXNodeInstance;
      if (vfxNode.contextId) {
        const ctx = graph._contexts.get(vfxNode.contextId);
        if (ctx) {
          (ctx.nodeIds as string[]).push(vfxNode.id);
        }
      }
    }

    // Restore edges
    for (const edge of data.edges) {
      graph.addEdgeWithId(edge);
    }

    // Restore context flow edges
    if (data.contextFlowEdges) {
      for (const edge of data.contextFlowEdges) {
        graph._contextFlowEdges.set(edge.id, edge);
      }
    }
    
    // Restore context settings
    if (data.contextSettings) {
      for (const [id, settings] of Object.entries(data.contextSettings)) {
        graph._contextSettings.set(id, settings);
      }
    }

    return graph;
  }

  // ============================================================================
  // Helpers
  // ============================================================================

  private _getContextType(defId: string): VFXContextType | undefined {
    for (const [type, def] of Object.entries(VFX_CONTEXTS)) {
      if (def.id === defId) return type as VFXContextType;
    }
    return undefined;
  }
}
